import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ExternalLink, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { cn } from '../lib/utils';

interface ArticleReferencesProps {
  sources: string[];
}

export function ArticleReferences({ sources }: ArticleReferencesProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  // Extract domain from URL for display
  const getDomain = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Format URL for display title
  const getDisplayTitle = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname.split('/').filter(Boolean).join(' › ');
      const domain = urlObj.hostname.replace('www.', '');
      return path ? `${domain} › ${path}` : domain;
    } catch {
      return url;
    }
  };

  return (
    <Card className="mt-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex w-full items-center justify-between text-left hover:opacity-80 transition-opacity">
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                References & Sources ({sources.length})
              </CardTitle>
              <ChevronDown
                className={cn(
                  "h-5 w-5 transition-transform duration-200",
                  isOpen && "rotate-180"
                )}
              />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        
        <CollapsibleContent>
          <CardContent>
            <div className="space-y-3">
              {sources.map((url, index) => (
                <div
                  key={url}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors"
                >
                  <span className="text-sm font-medium text-muted-foreground min-w-[2rem]">
                    [{index + 1}]
                  </span>
                  <div className="flex-1 min-w-0">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline block truncate"
                      title={url}
                    >
                      {getDisplayTitle(url)}
                    </a>
                    <p className="text-xs text-muted-foreground mt-1 truncate" title={url}>
                      {getDomain(url)}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t">
              {sources.length} {sources.length === 1 ? 'source' : 'sources'} used in research
            </p>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}